import React from 'react';
import {
  Datagrid,
  FunctionField,
  Identifier,
  Pagination,
  ReferenceField,
  ReferenceManyField,
  SimpleShowLayout,
  TextField,
  TextInput,
  required,
  useListContext,
  useRecordContext,
} from 'react-admin';
import { Typography } from '@mui/material';
import _ from 'lodash';
import LinkField from 'components/LinkField';
import LocationInput from 'components/LocationInput';
import PositionInput from 'components/PositionInput';
import QuickCreateButton from 'components/QuickCreateButton';
import SpecialtyInput from 'components/SpecialtyInput';
import useCheckAccess from 'hooks/useCheckAccess';
import { locationPositionFilter, scopeLabel } from 'utils/hrisMappingHelpers';

const MAPPED = {
  HrisPositionMapping: {
    label: 'Position',
    source: 'position.display_name',
    attribute: 'position_id',
  },
  HrisSpecialtyMapping: {
    label: 'Specialty',
    source: 'specialty.label',
    attribute: 'specialty_id',
  },
};

export type HrisMappingReference = keyof typeof MAPPED;

const EmptyMappings = ({ text }: { text: string }) => {
  const { error } = useListContext();

  return (
    <Typography variant="body2" color={error ? 'error' : 'textSecondary'} sx={{ p: 1 }}>
      {error ? 'Could not load mappings' : text}
    </Typography>
  );
};

const MappingsPagination = () => {
  const { error, total } = useListContext();

  return error || !total ? null : <Pagination />;
};

interface GridProps {
  reference: HrisMappingReference;
  target: string;
  emptyText: string;
  label?: string;
  showScope?: boolean;
}

const HrisMappingsGrid = ({ reference, target, emptyText, label = '', showScope }: GridProps) => {
  const mapped = MAPPED[reference];

  return (
    <ReferenceManyField
      label={label}
      pagination={<MappingsPagination />}
      reference={reference}
      target={target}
    >
      <Datagrid
        bulkActionButtons={false}
        rowClick="show"
        empty={<EmptyMappings text={emptyText} />}
      >
        <LinkField label="ID" source="id" />
        <TextField label="External ID" source="external_id" sortable={false} />
        <LinkField label={mapped.label} source={mapped.source} sortable={false} />
        {showScope && <FunctionField label="Scope" render={scopeLabel} />}
        {showScope && (
          <ReferenceField
            label="Location"
            source="location_id"
            reference="Location"
            link="show"
            sortable={false}
            emptyText="-"
          >
            <TextField source="name" />
          </ReferenceField>
        )}
      </Datagrid>
    </ReferenceManyField>
  );
};

interface CreateButtonProps {
  reference: HrisMappingReference;
  companyId: Identifier;
  locationId?: Identifier;
  subheader: string;
}

const CreateMappingButton = ({
  reference,
  companyId,
  locationId,
  subheader,
}: CreateButtonProps) => {
  const checkAccess = useCheckAccess();
  const { label, attribute } = MAPPED[reference];
  const isSpecialty = reference === 'HrisSpecialtyMapping';

  if (!checkAccess('create', reference, ['company_id', 'location_id', attribute, 'external_id'])) {
    return null;
  }

  return (
    <QuickCreateButton
      resource={reference}
      title={`Create HRIS ${label} Mapping`}
      subheader={subheader}
      initialValues={{ company_id: companyId, location_id: locationId }}
      transform={isSpecialty ? (values) => _.omit(values, 'position_id') : undefined}
    >
      {!locationId && (
        <LocationInput
          filter={{ company_id: companyId }}
          helperText="Leave empty for a company default"
        />
      )}
      <PositionInput
        validate={[required()]}
        filter={locationPositionFilter(locationId)}
        helperText={isSpecialty ? 'Filters the specialty list' : undefined}
      />
      {isSpecialty && (
        <SpecialtyInput
          positionSource="position_id"
          forJobs={false}
          activeOnly
          validate={[required()]}
        />
      )}
      <TextInput label="External ID" source="external_id" validate={[required()]} />
    </QuickCreateButton>
  );
};

export const CompanyHrisMappingsTab = ({ reference }: { reference: HrisMappingReference }) => {
  const company = useRecordContext();

  return (
    <SimpleShowLayout>
      <CreateMappingButton
        reference={reference}
        companyId={company.id}
        subheader={`Company: ${company.name}`}
      />
      <HrisMappingsGrid
        reference={reference}
        target="company_id"
        emptyText={`No HRIS ${MAPPED[reference].label.toLowerCase()} mappings`}
        showScope
      />
    </SimpleShowLayout>
  );
};

export const LocationHrisMappingsTab = ({ reference }: { reference: HrisMappingReference }) => {
  const location = useRecordContext();

  return (
    <SimpleShowLayout>
      <CreateMappingButton
        reference={reference}
        companyId={location.company_id}
        locationId={location.id}
        subheader={`Location: ${location.name}`}
      />
      <HrisMappingsGrid
        reference={reference}
        target="location_id"
        label="Location overrides"
        emptyText="No location overrides"
      />
      <HrisMappingsGrid
        reference={reference}
        target="search.company_default_mappings"
        label="Company defaults (not overridden by this location)"
        emptyText="No company defaults"
      />
    </SimpleShowLayout>
  );
};
